import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { FaSignOutAlt, FaHome, FaTrophy, FaUser, FaCommentDots } from 'react-icons/fa';

type AppHeaderProps = {
  onSignOut: () => void;
};

function AppHeader({ onSignOut }: AppHeaderProps) {
  const location = useLocation();
  const [isMobile, setIsMobile] = React.useState<boolean>(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const pillBase: React.CSSProperties = {
    color: '#e5e7eb',
    textDecoration: 'none',
    padding: isMobile ? '0.3rem 0.6rem' : '0.4rem 0.8rem',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: isMobile ? '0.7rem' : '0.82rem',
    transition: 'all .2s ease',
    border: '1px solid rgba(255,255,255,0.08)'
  };

  const brandStyle: React.CSSProperties = {
    fontWeight: 800,
    letterSpacing: '.5px',
    fontSize: isMobile ? '1.1rem' : '1.5rem',
    background: 'linear-gradient(90deg, #a78bfa 0%, #22d3ee 100%)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    textShadow: '0 0 24px rgba(34, 211, 238, 0.25)',
    whiteSpace: 'nowrap'
  } as React.CSSProperties;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000
    }}>
      <header style={{
        width: '100%',
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: isMobile ? '10px 1rem' : '12px 5vw',
        borderRadius: 0,
        background: 'linear-gradient(180deg, rgba(17,24,39,0.85) 0%, rgba(31,41,55,0.75) 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 10px 30px rgba(2,6,23,.6)',
        backdropFilter: 'blur(10px)'
      }}>
        <Link to="/" style={{ 
          ...brandStyle, 
          cursor: 'pointer', 
          textDecoration: 'none'
        }}>Boun Chess</Link>

        <nav style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '4px' : '8px',
          flexWrap: 'nowrap'
        }}>
          <Link
            to="/"
            style={{
              ...pillBase,
              background: isActive('/')
                ? 'linear-gradient(135deg, rgba(79,70,229,.5) 0%, rgba(6,182,212,.5) 100%)'
                : 'rgba(255,255,255,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '0.4rem' : '0.4rem 0.8rem',
              minWidth: isMobile ? '36px' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!isActive('/')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }
            }}
            title="Home"
          >
            {isMobile ? <FaHome /> : 'Home'}
          </Link>

          <Link
            to="/leaderboard"
            style={{
              ...pillBase,
              background: isActive('/leaderboard')
                ? 'linear-gradient(135deg, rgba(79,70,229,.5) 0%, rgba(6,182,212,.5) 100%)'
                : 'rgba(255,255,255,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '0.4rem' : '0.4rem 0.8rem',
              minWidth: isMobile ? '36px' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!isActive('/leaderboard')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/leaderboard')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }
            }}
            title="Leaderboard"
          >
            {isMobile ? <FaTrophy /> : 'Leaderboard'}
          </Link>

          <Link
            to="/profile"
            style={{
              ...pillBase,
              background: isActive('/profile')
                ? 'linear-gradient(135deg, rgba(79,70,229,.5) 0%, rgba(6,182,212,.5) 100%)'
                : 'rgba(255,255,255,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: isMobile ? '0.4rem' : '0.4rem 0.8rem',
              minWidth: isMobile ? '36px' : 'auto'
            }}
            onMouseEnter={(e) => {
              if (!isActive('/profile')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive('/profile')) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }
            }}
            title="Profile"
          >
            {isMobile ? <FaUser /> : 'Profile'}
          </Link>

          <Link
            to="/feedback"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? 0 : '6px',
              marginLeft: isMobile ? '4px' : '6px',
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              padding: isMobile ? '0.4rem' : '6px 12px',
              background: isActive('/feedback')
                ? 'rgba(96,165,250,0.16)'
                : 'rgba(96,165,250,0.08)',
              border: isActive('/feedback')
                ? '1px solid rgba(96,165,250,0.36)'
                : '1px solid rgba(96,165,250,0.28)',
              color: '#bfdbfe',
              borderRadius: '8px',
              boxShadow: 'none',
              minWidth: isMobile ? '36px' : 'auto',
              textDecoration: 'none',
              transition: 'all .2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(96,165,250,0.16)';
              e.currentTarget.style.borderColor = 'rgba(96,165,250,0.36)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = isActive('/feedback')
                ? 'rgba(96,165,250,0.16)'
                : 'rgba(96,165,250,0.08)';
              e.currentTarget.style.borderColor = isActive('/feedback')
                ? 'rgba(96,165,250,0.36)'
                : 'rgba(96,165,250,0.28)';
            }}
            title="Feedback"
          >
            <FaCommentDots />
            {!isMobile && 'Feedback'}
          </Link>

          <Button
            variant="secondary"
            onClick={onSignOut}
            style={{
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: isMobile ? '0' : '6px', 
              marginLeft: isMobile ? '4px' : '6px', 
              fontSize: isMobile ? '0.7rem' : '0.8rem', 
              padding: isMobile ? '0.4rem' : '6px 12px',
              background: 'rgba(239,68,68,0.06)',
              border: '1px solid rgba(239,68,68,0.24)',
              color: '#e5e7eb',
              borderRadius: '8px',
              boxShadow: 'none',
              minWidth: isMobile ? '36px' : 'auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
              e.currentTarget.style.borderColor = 'rgba(239,68,68,0.32)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239,68,68,0.06)';
              e.currentTarget.style.borderColor = 'rgba(239,68,68,0.24)';
            }}
            title="Sign Out"
          >
            <FaSignOutAlt style={{ color: '#ef4444' }} /> {!isMobile && 'Sign Out'}
          </Button>
        </nav>
      </header>
    </div>
  );
}

export default AppHeader;


